import React, { startTransition, useState } from 'react';
import { AvailabilityCalendar } from '../../components/AvailabilityCalendar';
import { MainLayout } from '../../components/MainLayout';
import { Loading } from '../../components/Loading';
import { useAvailabilityBlocks } from '../../utils/Hooks/useAvailabilityBlocks';
import { useCavallunaShows } from '../../utils/Hooks/useCavallunaShows';
import { useEvents } from '../../utils/Hooks/useEvents';
import { apiFetch } from '../../utils/network';
import styles from '../../utils/styles/pages/Studio/style.module.css';

function Studio({ previewMode = false }) {
  const { events, loading, error, refreshEvents } = useEvents();
  const {
    availabilityBlocks,
    loading: availabilityLoading,
    error: availabilityError,
    refreshAvailabilityBlocks
  } = useAvailabilityBlocks();
  const { shows, loading: showsLoading, error: showsError, refreshShows } = useCavallunaShows();
  const [form, setForm] = useState({
    nom: '',
    date_de_debut: '',
    date_de_fin: '',
    lien: '',
    imageUrl: ''
  });
  const [availabilityForm, setAvailabilityForm] = useState({
    titre: '',
    date_de_debut: '',
    date_de_fin: '',
    note: ''
  });
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [availabilityStatus, setAvailabilityStatus] = useState({ type: 'idle', message: '' });

  const navItems = previewMode ? [] : [
    { id: 'nav-back-home', label: "Retour à l'ouverture", onClick: () => { window.location.hash = '/'; } },
    { id: 'nav-analytics', label: 'Analytics', onClick: () => { window.location.hash = '/analytics'; } },
    {
      id: 'nav-refresh',
      label: 'Rafraîchir',
      onClick: () => {
        refreshEvents();
        refreshAvailabilityBlocks();
        refreshShows();
      }
    }
  ];

  const orderedEvents = [...events].sort((left, right) => {
    return `${left.date_de_debut}`.localeCompare(`${right.date_de_debut}`);
  });

  const onFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onAvailabilityFieldChange = (event) => {
    const { name, value } = event.target;
    setAvailabilityForm((current) => ({ ...current, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!form.nom || !form.date_de_debut || !form.date_de_fin || !form.lien || !form.imageUrl) {
      setStatus({ type: 'error', message: "Tous les champs et l'URL de l'image sont requis." });
      return;
    }

    setStatus({ type: 'loading', message: 'Publication en cours...' });

    try {
      const response = await apiFetch('/evenements/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        throw new Error('upload_failed');
      }

      setForm({
        nom: '',
        date_de_debut: '',
        date_de_fin: '',
        lien: '',
        imageUrl: ''
      });

      startTransition(() => {
        setStatus({ type: 'success', message: 'Événement ajouté avec succès.' });
      });

      refreshEvents();
    } catch (submitError) {
      setStatus({ type: 'error', message: 'Échec de publication. Vérifiez le backend.' });
    }
  };

  const onAvailabilitySubmit = async (event) => {
    event.preventDefault();

    if (!availabilityForm.titre || !availabilityForm.date_de_debut || !availabilityForm.date_de_fin) {
      setAvailabilityStatus({ type: 'error', message: 'Le titre et les dates sont requis.' });
      return;
    }

    setAvailabilityStatus({ type: 'loading', message: 'Mise à jour du calendrier...' });

    try {
      const response = await apiFetch('/disponibilites/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(availabilityForm)
      });

      if (!response.ok) {
        throw new Error('upload_failed');
      }

      setAvailabilityForm({
        titre: '',
        date_de_debut: '',
        date_de_fin: '',
        note: ''
      });

      startTransition(() => {
        setAvailabilityStatus({ type: 'success', message: 'Période ajoutée avec succès.' });
      });

      refreshAvailabilityBlocks();
    } catch (submitError) {
      setAvailabilityStatus({ type: 'error', message: 'Échec de mise à jour. Vérifiez le backend.' });
    }
  };

  const onDeleteAvailability = async (id) => {
    setAvailabilityStatus({ type: 'loading', message: 'Suppression en cours...' });

    try {
      const response = await apiFetch('/disponibilites/deleted', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });

      if (!response.ok) {
        throw new Error('delete_failed');
      }

      startTransition(() => {
        setAvailabilityStatus({ type: 'success', message: 'Période supprimée avec succès.' });
      });

      refreshAvailabilityBlocks();
    } catch (deleteError) {
      setAvailabilityStatus({ type: 'error', message: 'Échec de suppression. Vérifiez le backend.' });
    }
  };

  const orderedAvailabilityBlocks = [...availabilityBlocks].sort((left, right) => {
    return `${left.date_de_debut}`.localeCompare(`${right.date_de_debut}`);
  });

  return (
    <MainLayout
      eyebrow="Coulisses"
      navItems={navItems}
      previewMode={previewMode}
      showFooter={!previewMode}
      showNavigation={!previewMode}
      subtitle="Ajoutez affiches, dates et liens officiels pour garder la scène vivante."
      title="Orchestrer les dates."
    >
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.copy}>
            <span className={styles.kicker}>Publication</span>
            <h2 className={styles.title}>Ajouter un nouvel événement à l’agenda.</h2>
            <p className={styles.text}>
              Renseignez le titre, les dates, le lien public et l’URL de l’image, afin d’alimenter
              l’actualité du site.
            </p>
          </div>
          <div className={styles.panel}>
            <form className={styles.form} onSubmit={onSubmit}>
              <label className={styles.field}>
                <span>Nom de l’événement</span>
                <input name="nom" onChange={onFieldChange} value={form.nom} />
              </label>
              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Date de début</span>
                  <input
                    name="date_de_debut"
                    onChange={onFieldChange}
                    type="date"
                    value={form.date_de_debut}
                  />
                </label>
                <label className={styles.field}>
                  <span>Date de fin</span>
                  <input
                    name="date_de_fin"
                    onChange={onFieldChange}
                    type="date"
                    value={form.date_de_fin}
                  />
                </label>
              </div>
              <label className={styles.field}>
                <span>Lien de diffusion</span>
                <input name="lien" onChange={onFieldChange} type="url" value={form.lien} />
              </label>
              <label className={styles.field}>
                <span>URL de l'image</span>
                <input
                  name="imageUrl"
                  onChange={onFieldChange}
                  placeholder="https://..."
                  type="url"
                  value={form.imageUrl}
                />
              </label>
              {form.imageUrl ? (
                <div className={styles.previewFrame}>
                  <img alt="Aperçu de l’affiche" className={styles.preview} src={form.imageUrl} />
                </div>
              ) : null}
              <button className={styles.submit} type="submit">
                Publier l’événement
              </button>
              {status.type !== 'idle' ? (
                <p className={styles[`status${status.type}`]}>{status.message}</p>
              ) : null}
            </form>
          </div>
        </section>

        <section className={styles.hero}>
          <div className={styles.copy}>
            <span className={styles.kicker}>Disponibilités</span>
            <h2 className={styles.title}>Ajuster le calendrier de Sylvie.</h2>
            <p className={styles.text}>
              Ajoutez ici les périodes d’indisponibilité hors représentation. Le calendrier public
              fusionnera automatiquement ces périodes avec les dates de spectacle déjà engagées.
            </p>
          </div>
          <div className={styles.panel}>
            <form className={styles.form} onSubmit={onAvailabilitySubmit}>
              <label className={styles.field}>
                <span>Titre de la période</span>
                <input name="titre" onChange={onAvailabilityFieldChange} value={availabilityForm.titre} />
              </label>
              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Date de début</span>
                  <input
                    name="date_de_debut"
                    onChange={onAvailabilityFieldChange}
                    type="date"
                    value={availabilityForm.date_de_debut}
                  />
                </label>
                <label className={styles.field}>
                  <span>Date de fin</span>
                  <input
                    name="date_de_fin"
                    onChange={onAvailabilityFieldChange}
                    type="date"
                    value={availabilityForm.date_de_fin}
                  />
                </label>
              </div>
              <label className={styles.field}>
                <span>Note</span>
                <textarea
                  name="note"
                  onChange={onAvailabilityFieldChange}
                  placeholder="Déplacement, résidence, préparation, congés..."
                  rows="4"
                  value={availabilityForm.note}
                />
              </label>
              <button className={styles.submit} type="submit">
                Ajouter la période
              </button>
              {availabilityStatus.type !== 'idle' ? (
                <p className={styles[`status${availabilityStatus.type}`]}>{availabilityStatus.message}</p>
              ) : null}
            </form>
          </div>
        </section>

        <section className={styles.listSection}>
          <div className={styles.listHeader}>
            <span className={styles.kicker}>Calendrier public</span>
            <h2 className={styles.listTitle}>Aperçu des disponibilités.</h2>
          </div>
          {showsLoading || availabilityLoading ? <Loading label="Chargement du calendrier..." /> : null}
          {showsError || availabilityError ? (
            <p className={styles.statuserror}>Le chargement du calendrier a échoué.</p>
          ) : null}
          {!showsLoading && !availabilityLoading && !showsError && !availabilityError ? (
            <AvailabilityCalendar availabilityBlocks={availabilityBlocks} minimumMonths={8} shows={shows} />
          ) : null}
        </section>

        <section className={styles.listSection}>
          <div className={styles.listHeader}>
            <span className={styles.kicker}>Agenda en ligne</span>
            <h2 className={styles.listTitle}>Événements déjà publiés.</h2>
          </div>
          {loading ? <Loading label="Chargement des événements..." /> : null}
          {error ? <p className={styles.statuserror}>Le chargement des événements a échoué.</p> : null}
          <div className={styles.grid}>
            {orderedEvents.map((eventItem) => (
              <article className={styles.card} key={eventItem.id || eventItem._id}>
                <img
                  alt={eventItem.nom}
                  className={styles.cardImage}
                  src={eventItem.imageUrl || eventItem.chemin_de_limage}
                />
                <div className={styles.cardCopy}>
                  <p className={styles.cardMeta}>
                    {eventItem.date_de_debut} · {eventItem.date_de_fin}
                  </p>
                  <h3 className={styles.cardTitle}>{eventItem.nom}</h3>
                  <a href={eventItem.lien} rel="noreferrer" target="_blank">
                    Ouvrir le lien
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.listSection}>
          <div className={styles.listHeader}>
            <span className={styles.kicker}>Indisponibilités</span>
            <h2 className={styles.listTitle}>Périodes déjà enregistrées.</h2>
          </div>
          {availabilityLoading ? <Loading label="Chargement des indisponibilités..." /> : null}
          {availabilityError ? <p className={styles.statuserror}>Le chargement des indisponibilités a échoué.</p> : null}
          <div className={styles.blocksList}>
            {orderedAvailabilityBlocks.map((block) => (
              <article className={styles.blockCard} key={block.id || block._id}>
                <div className={styles.blockCopy}>
                  <p className={styles.cardMeta}>
                    {block.date_de_debut} · {block.date_de_fin}
                  </p>
                  <h3 className={styles.cardTitle}>{block.titre}</h3>
                  {block.note ? <p className={styles.blockNote}>{block.note}</p> : null}
                </div>
                <button className={styles.deleteButton} onClick={() => onDeleteAvailability(block.id)} type="button">
                  Supprimer
                </button>
              </article>
            ))}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}

export default Studio;
